import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { PaxSelectorContentRow } from './ContentRow';
import { ABUtils, Config, TrackingUtils, withPageContext } from '../../utils';
import { MaxPaxProps, MaxPaxPropsDefaults, PaxProps } from '../../models';
import { setPax } from '../../redux/currentSearch/actions';
import { ErrorMessage } from './ErrorMessage';
import { getResidentDiscountProps } from '../../redux/currentSearch/selectors';

const PaxSelectorContent = ({ pax, residentProps, setPax, trackEvent, maxPax }) => {
    const { adults, kids, infants } = pax;
    const { maxPassengers, maxChildrenPerAdult, maxInfantsPerAdult } = maxPax;
    const { isCssVariationMobile, isResidentDiscountPaxType } = ABUtils.getABs();
    const residentZones = Config.get('residentZones');

    const getPaxRanges = () => {
        const minAdults = Math.max(Math.round((kids + infants) / maxChildrenPerAdult), infants, 1);
        return {
            maxAdults: maxPassengers - kids - infants,
            maxKids: Math.min(maxPassengers - adults - infants, adults * maxChildrenPerAdult - infants),
            maxInfants: Math.min(maxPassengers - adults - kids, adults * maxChildrenPerAdult - kids, adults * maxInfantsPerAdult),
            minAdults,
        };
    };

    const paxRange = getPaxRanges();

    const onPaxChange = (partial) => {
        const newPax = { ...pax, ...partial };
        const { minAdults } = paxRange;
        newPax.adults = Math.max(minAdults, newPax.adults);
        setPax(newPax);
    };

    const getValidationMessage = () => {
        let validationMessage = '';
        if ((adults + kids + infants) === maxPassengers) {
            validationMessage = 'paxSelector.info';
        } else if (adults * maxChildrenPerAdult === kids) {
            validationMessage = 'paxSelector.children.info';
        } else if (adults * maxInfantsPerAdult === infants) {
            validationMessage = 'paxSelector.infants.info';
        }
        return validationMessage;
    };

    const residentZoneFlight = residentZones && residentProps.regions.length;

    const getResidentMessage = () => {
        return residentZoneFlight && (adults + kids + infants > 1)
            ? 'residentDiscount.main.ifSomePassengers'
            : '';
    };

    const getResidentChildrenMessages = () => {
        const regionsText = residentProps && residentProps.regions && residentProps.regions.join(' o ');
        return residentZoneFlight && (kids + infants > 0) && !isResidentDiscountPaxType
            ? [{ key: 'residentDiscount.widget.pluskids.title', values: { residentZone: regionsText }, dangerouslySetInnerHTML: true }]
            : [];
    };

    const validationMessage = getValidationMessage();
    const residentDefaultMessage = getResidentMessage();
    const residentChildrenMessages = getResidentChildrenMessages();

    if (validationMessage) {
        const label = TrackingUtils.LABEL.numberPaxInfo(validationMessage);
        trackEvent({ action: TrackingUtils.ACTION.FLIGHTS, label });
    }

    return (
        <>
            <PaxSelectorContentRow
                paxKey="adults"
                cmsKey="adults"
                min={paxRange.minAdults}
                max={paxRange.maxAdults}
                value={adults}
                onChange={onPaxChange}
            />
            {isCssVariationMobile && (
                <div className="odf-separator-dotted" />
            )}
            <PaxSelectorContentRow
                paxKey="kids"
                cmsKey="children"
                max={paxRange.maxKids}
                value={kids}
                onChange={onPaxChange}
            />
            {isCssVariationMobile && (
                <div className="odf-separator-dotted" />
            )}
            <PaxSelectorContentRow
                paxKey="infants"
                cmsKey="infants"
                max={paxRange.maxInfants}
                value={infants}
                onChange={onPaxChange}
            />
            {validationMessage && <ErrorMessage message={validationMessage} />}
            {residentDefaultMessage && (
                <ErrorMessage
                    message={residentDefaultMessage}
                    messages={residentChildrenMessages}
                    type="odf-message-informative-highlight odf-msg-informative odf-space-inner-s"
                />
            )}
        </>
    );
};

PaxSelectorContent.propTypes = {
    pax: PaxProps.isRequired,
    maxPax: MaxPaxProps,
    setPax: PropTypes.func.isRequired,
    trackEvent: PropTypes.func.isRequired,
    residentProps: PropTypes.object.isRequired,
};

PaxSelectorContent.defaultProps = {
    maxPax: MaxPaxPropsDefaults,
};

const withContext = withPageContext(PaxSelectorContent);
export { withContext as PaxSelectorContent };

const mapStateToProps = createStructuredSelector({
    pax: ({ currentSearch }) => currentSearch.pax,
    residentProps: getResidentDiscountProps,
});

export const ConnectedPaxSelectorContent = connect(mapStateToProps, {
    setPax,
})(withContext);
